import { DataSource, ISODate, PeriodType } from "@inrev/common";
import { DateTime } from "luxon";
import { useMemo } from "react";
import type { BalanceSheet, FinancialPeriodDto, IncomeStatement } from "../utils";
import { sortFinancialPeriods } from "../utils/sortFinancialPeriods";

export type FinancialPeriodBundle = {
	period: FinancialPeriodDto;
	profitable?: boolean; // Needed for manual profitability edits
	blocSize?: number; // Needed for manual BLOC size edits
	incomeStatement?: IncomeStatement;
	balanceSheet?: BalanceSheet;
	isManual: boolean; // Manual income statement, or a manual-only period with no statements
};

export type FinancialsMetrics = {
	recentFYE?: FinancialPeriodBundle;
	previousFYE?: FinancialPeriodBundle;
	recentYTD?: FinancialPeriodBundle;
};

const findFirstActiveById = <T extends { id: string }>(
	slotPeriods: FinancialPeriodDto[],
	statements: T[],
	predicate: (period: FinancialPeriodDto, statement: T) => boolean,
): T | undefined => {
	return slotPeriods.reduce<T | undefined>((found, period) => {
		if (found) {
			return found;
		}

		return statements.find((statement) => predicate(period, statement));
	}, undefined);
};

const slotKey = (period: FinancialPeriodDto): string =>
	`${period.periodType}::${period.periodEndDate}`;

const groupBySlotKey = (periods: FinancialPeriodDto[]): FinancialPeriodDto[][] => {
	const groups = new Map<string, FinancialPeriodDto[]>();

	for (const period of periods) {
		const key = slotKey(period);
		const existing = groups.get(key);

		if (existing) {
			existing.push(period);
		} else {
			groups.set(key, [period]);
		}
	}

	return [...groups.values()];
};

const buildSlotBundle = ({
	slotPeriods,
	incomeStatements,
	balanceSheets,
}: {
	slotPeriods: FinancialPeriodDto[];
	incomeStatements: IncomeStatement[];
	balanceSheets: BalanceSheet[];
}): FinancialPeriodBundle => {
	const incomeStatement = findFirstActiveById(
		slotPeriods,
		incomeStatements,
		(period, statement) => period.activeIncomeStatementId === statement.id,
	);

	const balanceSheet = findFirstActiveById(
		slotPeriods,
		balanceSheets,
		(period, statement) => period.activeBalanceSheetId === statement.id,
	);

	const profitable = slotPeriods.find((p) => p.profitable != null)?.profitable;
	const isManual = incomeStatement
		? incomeStatement.sourceDataSource === DataSource.manual
		: profitable != null;

	return {
		period: slotPeriods[0],
		profitable,
		blocSize: slotPeriods.find((p) => p.blocSize != null)?.blocSize,
		incomeStatement,
		balanceSheet,
		isManual,
	};
};

const isMeaningful = (bundle: FinancialPeriodBundle): boolean =>
	bundle.incomeStatement !== undefined ||
	bundle.balanceSheet !== undefined ||
	bundle.profitable != null ||
	bundle.blocSize != null;

const hasStatement = (bundle: FinancialPeriodBundle): boolean =>
	bundle.incomeStatement !== undefined || bundle.balanceSheet !== undefined;

// Prefer the most recent slot backed by real statement data so a newer manual-only
// period (no balance sheet / income statement, not shown in the period selector) can't
// mask an older FYE that actually has data. Fall back to the most recent manual-only
// slot only when no slot has statements, so empty cards remain editable.
const pickRecent = (bundles: FinancialPeriodBundle[]): FinancialPeriodBundle | undefined =>
	bundles.find(hasStatement) ?? bundles[0];

const yearOf = (periodEndDate?: ISODate): number | undefined =>
	periodEndDate ? DateTime.fromISO(periodEndDate).year : undefined;

export const useFinancialsMetrics = (
	periods: FinancialPeriodDto[],
	incomeStatements: IncomeStatement[],
	balanceSheets: BalanceSheet[],
): FinancialsMetrics => {
	return useMemo(() => {
		const sortedPeriods = sortFinancialPeriods(periods);

		const bundles = groupBySlotKey(sortedPeriods).reduce<FinancialPeriodBundle[]>(
			(acc, slotPeriods) => {
				const bundle = buildSlotBundle({ slotPeriods, incomeStatements, balanceSheets });
				return isMeaningful(bundle) ? [...acc, bundle] : acc;
			},
			[],
		);

		const fyeBundles = bundles.filter((b) => b.period.periodType === PeriodType.Fye);
		const ytdBundles = bundles.filter((b) => b.period.periodType === PeriodType.Ytd);

		const recentFYE = pickRecent(fyeBundles);
		const recentFYEYear = recentFYE ? yearOf(recentFYE.period.periodEndDate) : undefined;
		const previousFYE =
			recentFYEYear !== undefined
				? pickRecent(fyeBundles.filter((b) => yearOf(b.period.periodEndDate) === recentFYEYear - 1))
				: undefined;

		return {
			recentFYE,
			previousFYE,
			// Unlike FYE, YTD intentionally takes the most recent slot outright (no pickRecent):
			// a YTD is a current-state snapshot, so a fresh manual entry should supersede an
			// older extracted YTD rather than be masked by it.
			recentYTD: ytdBundles[0],
		};
	}, [periods, incomeStatements, balanceSheets]);
};
